(function () {
  const qs = (selector, scope) => (scope || document).querySelector(selector);
  const qsa = (selector, scope) => Array.from((scope || document).querySelectorAll(selector));
  const shopRoot = () => (window.Shopify && Shopify.routes ? Shopify.routes.root : '/');
  const currency = () => (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';

  const header = qs('[data-header]');
  const onScroll = () => { if (header) header.classList.toggle('is-scrolled', window.scrollY > 8); };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  const menuToggle = qs('[data-menu-toggle]');
  const siteNav = qs('[data-site-nav]');
  const closeMenu = (restoreFocus) => {
    if (!menuToggle || !siteNav) return;
    siteNav.classList.remove('is-open');
    menuToggle.setAttribute('aria-expanded', 'false');
    const label = qs('.sr-only', menuToggle);
    if (label) label.textContent = 'Open menu';
    if (restoreFocus) menuToggle.focus();
  };
  if (menuToggle && siteNav) {
    menuToggle.addEventListener('click', () => {
      const isOpen = siteNav.classList.toggle('is-open');
      menuToggle.setAttribute('aria-expanded', String(isOpen));
      const label = qs('.sr-only', menuToggle);
      if (label) label.textContent = isOpen ? 'Close menu' : 'Open menu';
    });
    qsa('a', siteNav).forEach((link) => link.addEventListener('click', () => closeMenu(false)));
    document.addEventListener('click', (event) => {
      if (siteNav.classList.contains('is-open') && !siteNav.contains(event.target) && !menuToggle.contains(event.target)) closeMenu(false);
    });
  }

  qsa('[data-product-gallery]').forEach((gallery) => {
    const main = qs('[data-product-main-image]', gallery);
    qsa('[data-gallery-thumb]', gallery).forEach((thumb) => {
      thumb.addEventListener('click', () => {
        if (!main) return;
        main.src = thumb.dataset.imageSrc;
        main.removeAttribute('srcset');
        main.alt = thumb.dataset.imageAlt || '';
        qsa('[data-gallery-thumb]', gallery).forEach((item) => {
          const active = item === thumb;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-current', String(active));
        });
      });
    });
  });

  const toast = qs('[data-cart-toast]');
  const showToast = (message, isError) => {
    if (!toast) return;
    toast.textContent = message;
    toast.setAttribute('role', isError ? 'alert' : 'status');
    toast.setAttribute('aria-live', isError ? 'assertive' : 'polite');
    toast.classList.toggle('toast--error', Boolean(isError));
    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => toast.classList.remove('is-visible'), 4200);
  };

  const pushCommerceEvent = (event, ecommerce) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ecommerce });
  };
  const announceShopifyEvent = (name, detail) => document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency() }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };
  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));

  const cartDrawer = qs('[data-cart-drawer]');
  const cartOverlay = qs('[data-cart-overlay]');
  const cartTriggers = qsa('[data-cart-open]');
  let lastCartTrigger = null;
  let cartState = null;
  let overlayTimer = null;
  let cartPageDirty = false;

  const updateCartLabels = (count) => {
    qsa('[data-cart-count]').forEach((node) => { node.textContent = count; });
    cartTriggers.forEach((trigger) => trigger.setAttribute('aria-label', `View cart, ${count} ${count === 1 ? 'item' : 'items'}`));
  };

  const renderCartDrawer = (cart) => {
    if (!cartDrawer) return;
    cartState = cart;
    updateCartLabels(cart.item_count);
    const itemsNode = qs('[data-cart-drawer-items]', cartDrawer);
    const footer = qs('[data-cart-drawer-footer]', cartDrawer);
    const subtotal = qs('[data-cart-subtotal]', cartDrawer);
    const discountRow = qs('[data-cart-discount-row]', cartDrawer);
    const discount = qs('[data-cart-discount]', cartDrawer);
    const message = qs('[data-shipping-message]', cartDrawer);
    const bar = qs('[data-shipping-bar]', cartDrawer);
    const threshold = Number(cartDrawer.dataset.freeShippingThreshold || 0);

    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
    if (discountRow && discount) {
      discountRow.hidden = !(cart.total_discount > 0);
      discount.textContent = `−${formatMoney(cart.total_discount)}`;
    }
    if (footer) footer.hidden = cart.item_count === 0;
    if (message && bar && threshold > 0) {
      const remaining = Math.max(threshold - cart.total_price, 0);
      bar.style.width = `${Math.min((cart.total_price / threshold) * 100, 100)}%`;
      message.textContent = remaining === 0 ? 'Your order qualifies for free shipping.' : `${formatMoney(remaining)} away from free shipping.`;
    }

    if (!itemsNode) return;
    if (!cart.items.length) {
      itemsNode.innerHTML = `<div class="drawer-empty"><h3>Your cart is empty.</h3><p>Choose a PullAloud color to get started.</p><a class="button button--coral" href="${shopRoot()}#product" data-cart-close>Choose your color</a></div>`;
      return;
    }
    itemsNode.innerHTML = cart.items.map((item) => {
      const image = item.featured_image && item.featured_image.url ? item.featured_image.url : item.image;
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<span>${escapeHtml(item.variant_title)}</span>` : '';
      const original = item.original_line_price > item.final_line_price ? `<s>${formatMoney(item.original_line_price)}</s> ` : '';
      return `<article class="drawer-item" data-drawer-line data-line-key="${escapeHtml(item.key)}">
        ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(item.product_title)}" width="110" height="110" loading="lazy">` : ''}
        <div class="drawer-item__info"><a href="${escapeHtml(item.url)}"><strong>${escapeHtml(item.product_title)}</strong></a>${variant}<span>${original}${formatMoney(item.final_line_price)}</span>
          <div class="drawer-quantity" aria-label="Quantity controls for ${escapeHtml(item.product_title)}">
            <button type="button" data-cart-decrease data-line-key="${escapeHtml(item.key)}" aria-label="Decrease ${escapeHtml(item.product_title)} quantity">−</button>
            <span aria-live="polite">${item.quantity}</span>
            <button type="button" data-cart-increase data-line-key="${escapeHtml(item.key)}" aria-label="Increase ${escapeHtml(item.product_title)} quantity">+</button>
            <button class="drawer-remove" type="button" data-cart-remove data-line-key="${escapeHtml(item.key)}" aria-label="Remove ${escapeHtml(item.product_title)}">Remove</button>
          </div>
        </div>
      </article>`;
    }).join('');
  };

  const fetchCart = () => fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } })
    .then((response) => { if (!response.ok) throw new Error('Unable to load cart'); return response.json(); })
    .then((cart) => { renderCartDrawer(cart); return cart; });

  const focusableInDrawer = () => qsa('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', cartDrawer).filter((node) => !node.hidden && node.offsetParent !== null);
  const setBackgroundInert = (value) => qsa('[data-header], #MainContent, .site-footer').forEach((node) => { node.inert = value; });
  const openCartDrawer = () => {
    if (!cartDrawer || !cartOverlay) return;
    window.clearTimeout(overlayTimer);
    cartOverlay.hidden = false;
    requestAnimationFrame(() => { cartOverlay.classList.add('is-visible'); cartDrawer.classList.add('is-open'); });
    cartDrawer.setAttribute('aria-hidden', 'false');
    cartTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    document.body.classList.add('cart-drawer-open');
    setBackgroundInert(true);
    const close = qs('[data-cart-close]', cartDrawer);
    if (close) close.focus();
    if (cartState) pushCommerceEvent('view_cart', { currency: cartState.currency || currency(), value: cartState.total_price / 100, items: cartState.items.map((item) => ({ item_id: String(item.variant_id), item_name: item.product_title, item_variant: item.variant_title, price: item.final_price / 100, quantity: item.quantity })) });
    announceShopifyEvent('shopify:cart:view', { cart: cartState });
  };
  const closeCartDrawer = () => {
    if (!cartDrawer || !cartOverlay) return;
    cartDrawer.classList.remove('is-open');
    cartOverlay.classList.remove('is-visible');
    cartDrawer.setAttribute('aria-hidden', 'true');
    cartTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('cart-drawer-open');
    setBackgroundInert(false);
    overlayTimer = window.setTimeout(() => { cartOverlay.hidden = true; }, 240);
    if (lastCartTrigger && document.contains(lastCartTrigger)) lastCartTrigger.focus();
    if (cartPageDirty && document.body.classList.contains('template-cart')) window.location.reload();
  };
  const refreshCartDrawer = (openAfter) => fetchCart().then((cart) => { if (openAfter) openCartDrawer(); return cart; });

  cartTriggers.forEach((trigger) => trigger.addEventListener('click', (event) => {
    if (!cartDrawer) return;
    event.preventDefault();
    lastCartTrigger = trigger;
    refreshCartDrawer(true).catch(() => { window.location.href = trigger.href; });
  }));
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-cart-close]')) closeCartDrawer();
  });
  if (cartOverlay) cartOverlay.addEventListener('click', closeCartDrawer);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (cartDrawer && cartDrawer.classList.contains('is-open')) closeCartDrawer();
      else if (siteNav && siteNav.classList.contains('is-open')) closeMenu(true);
    }
    if (event.key === 'Tab' && cartDrawer && cartDrawer.classList.contains('is-open')) {
      const focusable = focusableInDrawer();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  });

  if (cartDrawer) cartDrawer.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-increase], [data-cart-decrease], [data-cart-remove]');
    if (!button || !cartState) return;
    const key = button.dataset.lineKey;
    const item = cartState.items.find((line) => line.key === key);
    if (!item) return;
    const nextQuantity = button.hasAttribute('data-cart-remove') ? 0 : Math.max(item.quantity + (button.hasAttribute('data-cart-increase') ? 1 : -1), 0);
    const line = button.closest('[data-drawer-line]');
    if (line) { line.setAttribute('aria-busy', 'true'); qsa('button', line).forEach((control) => { control.disabled = true; }); }
    fetch(`${shopRoot()}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity: nextQuantity }) })
      .then((response) => { if (!response.ok) throw new Error('Unable to update cart'); return response.json(); })
      .then((cart) => {
        if (nextQuantity === 0) pushCommerceEvent('remove_from_cart', { currency: cart.currency || currency(), value: item.final_line_price / 100, items: [{ item_id: String(item.variant_id), item_name: item.product_title, item_variant: item.variant_title, price: item.final_price / 100, quantity: item.quantity }] });
        renderCartDrawer(cart);
        cartPageDirty = true;
        announceShopifyEvent('shopify:cart:lines-update', { cart });
      })
      .catch(() => { showToast('We couldn’t update your cart. Please try again.', true); if (line) { line.removeAttribute('aria-busy'); qsa('button', line).forEach((control) => { control.disabled = false; }); } });
  });

  qsa('[data-checkout-button]').forEach((button) => button.addEventListener('click', () => {
    const ecommerce = cartState ? { currency: cartState.currency || currency(), value: cartState.total_price / 100, items: cartState.items.map((item) => ({ item_id: String(item.variant_id), item_name: item.product_title, item_variant: item.variant_title, price: item.final_price / 100, quantity: item.quantity })) } : {};
    pushCommerceEvent('begin_checkout', ecommerce);
  }));

  document.addEventListener('click', (event) => {
    const control = event.target.closest('[data-cart-page-increase], [data-cart-page-decrease], [data-cart-page-remove]');
    if (!control) return;
    const line = control.closest('[data-cart-page-line]');
    if (!line || line.getAttribute('aria-busy') === 'true') return;
    const currentQuantity = Number(line.dataset.lineQuantity || 0);
    const nextQuantity = control.hasAttribute('data-cart-page-remove') ? 0 : Math.max(currentQuantity + (control.hasAttribute('data-cart-page-increase') ? 1 : -1), 0);
    line.setAttribute('aria-busy', 'true');
    qsa('button', line).forEach((button) => { button.disabled = true; });
    fetch(`${shopRoot()}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: line.dataset.lineKey, quantity: nextQuantity }) })
      .then((response) => { if (!response.ok) throw new Error('Unable to update cart'); return response.json(); })
      .then((cart) => {
        renderCartDrawer(cart);
        announceShopifyEvent('shopify:cart:lines-update', { cart });
        if (!cart.item_count) { window.location.reload(); return; }
        const updatedItem = cart.items.find((item) => item.key === line.dataset.lineKey);
        if (!updatedItem) line.remove();
        else {
          line.dataset.lineQuantity = updatedItem.quantity;
          const quantityNode = qs('[data-cart-page-quantity]', line);
          const priceNode = qs('[data-cart-page-line-price]', line);
          if (quantityNode) quantityNode.textContent = updatedItem.quantity;
          if (priceNode) priceNode.innerHTML = `${updatedItem.original_line_price > updatedItem.final_line_price ? `<s>${formatMoney(updatedItem.original_line_price)}</s> ` : ''}${formatMoney(updatedItem.final_line_price)}`;
          line.removeAttribute('aria-busy');
          qsa('button', line).forEach((button) => { button.disabled = false; });
        }
        qsa('[data-cart-page-subtotal]').forEach((subtotal) => { subtotal.textContent = formatMoney(cart.total_price); });
      })
      .catch(() => { line.removeAttribute('aria-busy'); qsa('button', line).forEach((button) => { button.disabled = false; }); showToast('We couldn’t update your cart. Please try again.', true); });
  });

  const productSections = qsa('[data-product-section][data-product-id]');
  if (document.body.classList.contains('template-product') && productSections[0]) {
    const section = productSections[0];
    const variantInput = qs('[name="id"]', section);
    pushCommerceEvent('view_item', { currency: section.dataset.productCurrency || currency(), value: Number(section.dataset.productPrice || 0) / 100, items: [{ item_id: variantInput ? String(variantInput.value) : section.dataset.productId, item_name: section.dataset.productTitle, price: Number(section.dataset.productPrice || 0) / 100 }] });
  }

  const optionForControl = (control) => {
    if (!control) return null;
    if (control.tagName === 'SELECT') return control.options[control.selectedIndex] || null;
    return control;
  };
  const cleanVariantTitle = (value) => String(value || '').trim().replace(/\s+—\s+Sold out$/, '');
  const selectedColor = (info, slot) => {
    const control = qs(`[data-color-select][data-color-slot="${slot}"]`, info);
    const option = optionForControl(control);
    if (!option || !option.value) return null;
    return {
      id: String(option.value),
      title: cleanVariantTitle(option.textContent || option.dataset.variantTitle || ''),
      price: Number(option.dataset.priceRaw || info.closest('[data-product-section]')?.dataset.productPrice || 0),
      available: option.dataset.available !== 'false' && !option.disabled,
      imageSrc: option.dataset.imageSrc || ''
    };
  };
  const selectedOfferSize = (info) => Number(qs('[data-offer-option].is-active', info)?.dataset.offerSize || 1);
  const projectedOfferTotal = (info) => {
    const first = selectedColor(info, 1);
    const size = selectedOfferSize(info);
    if (!first) return 0;
    if (size === 1) return first.price;
    const second = selectedColor(info, 2) || first;
    const picker = qs('[data-offer-picker]', info);
    const discountIsLive = picker && picker.dataset.pairDiscountConfigured === 'true';
    const discount = discountIsLive ? Number(picker.dataset.pairDiscount || 0) : 0;
    return Math.round((first.price + second.price) * (100 - discount) / 100);
  };
  const refreshOfferPrices = (info) => {
    const picker = qs('[data-offer-picker]', info);
    const first = selectedColor(info, 1);
    if (!picker || !first) return;
    const second = selectedColor(info, 2) || first;
    const discountIsLive = picker.dataset.pairDiscountConfigured === 'true';
    const discount = discountIsLive ? Number(picker.dataset.pairDiscount || 0) : 0;
    qsa('[data-offer-option]', picker).forEach((option) => {
      const size = Number(option.dataset.offerSize || 1);
      const total = size === 1 ? first.price : Math.round((first.price + second.price) * (100 - discount) / 100);
      const price = qs('[data-offer-price]', option);
      if (price) price.textContent = formatMoney(total);
    });
  };
  const updateAddButton = (info) => {
    if (!info) return;
    const first = selectedColor(info, 1);
    const size = selectedOfferSize(info);
    const second = size === 2 ? selectedColor(info, 2) : null;
    const available = Boolean(first && first.available && (size === 1 || (second && second.available)));
    const total = projectedOfferTotal(info);
    const form = qs('form', info);
    if (form) form.dataset.offerSize = String(size);
    const panel = qs('[data-pair-color-panel]', info);
    if (panel) panel.hidden = size !== 2;
    const primaryLabel = qs('[data-color-selector][data-color-slot="1"] [data-color-label]', info);
    if (primaryLabel) primaryLabel.textContent = size === 2 ? 'Color 1' : 'Color';
    const note = qs('[data-offer-note]', info);
    if (note) {
      const picker = qs('[data-offer-picker]', info);
      const discountIsLive = picker && picker.dataset.pairDiscountConfigured === 'true';
      const discount = picker ? Number(picker.dataset.pairDiscount || 0) : 0;
      note.textContent = size === 2
        ? `Choose Color 1 and Color 2 independently.${discountIsLive ? ` ${discount}% is applied automatically in cart.` : ''}`
        : 'One alarm in the color you choose.';
    }
    refreshOfferPrices(info);
    const button = qs('[data-add-button]', info);
    if (button) {
      button.disabled = !available;
      const label = qs('[data-add-label]', button);
      const addPrice = qs('[data-add-price]', button);
      if (label) label.textContent = available ? (size === 2 ? 'Add 2 alarms to cart' : 'Add to cart') : 'Sold out';
      if (addPrice) addPrice.textContent = available ? `— ${formatMoney(total)}` : '';
    }
    const payment = qs('[data-payment-button]', info);
    if (payment) payment.hidden = size === 2;
    const mobileButton = qs('[data-mobile-add]');
    const mobilePrice = qs('[data-mobile-price]');
    const mobileVariant = qs('[data-mobile-variant]');
    if (mobileButton) { mobileButton.disabled = !available; const span = qs('span', mobileButton); if (span) span.textContent = available ? (size === 2 ? 'Add 2' : 'Add to cart') : 'Sold out'; }
    if (mobilePrice) mobilePrice.textContent = formatMoney(total);
    if (mobileVariant) mobileVariant.textContent = first ? (size === 2 && second ? `${first.title} + ${second.title}` : first.title) : 'PullAloud alarm';
  };

  qsa('[data-color-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const selected = optionForControl(select);
      const info = select.closest('.product-info');
      const section = select.closest('[data-product-section]');
      const gallery = section && qs('[data-product-gallery]', section);
      const slot = Number(select.dataset.colorSlot || 1);
      const selector = select.closest('[data-color-selector]');
      const label = selector && qs('[data-selected-variant-label]', selector);
      if (label && selected) label.textContent = selected.textContent.trim().replace(/\s+—\s+Sold out$/, '');
      if (slot === 1) {
        const price = qs('[data-product-price]', info);
        const compare = qs('[data-compare-price]', info);
        if (price && selected) price.textContent = selected.dataset.price || formatMoney(selected.dataset.priceRaw);
        if (compare && selected) { compare.textContent = selected.dataset.comparePrice || ''; compare.hidden = !selected.dataset.comparePrice; }
        if (selected && selected.dataset.imageSrc && gallery) {
          const main = qs('[data-product-main-image]', gallery);
          if (main) { main.src = selected.dataset.imageSrc; main.removeAttribute('srcset'); main.alt = `PullAloud ${cleanVariantTitle(selected.textContent)} personal alarm`; }
        }
      }
      if (selected && slot === 1) {
        pushCommerceEvent('select_variant', { currency: currency(), items: [{ item_id: String(selected.value), item_name: section ? section.dataset.productTitle : 'PullAloud', item_variant: selected.textContent.trim(), price: Number(selected.dataset.priceRaw || 0) / 100 }] });
        announceShopifyEvent('shopify:product:select', { variantId: selected.value });
        if (document.body.classList.contains('template-product') && window.history.replaceState) {
          const url = new URL(window.location.href); url.searchParams.set('variant', selected.value); window.history.replaceState({}, '', url);
        }
      }
      if (selector) qsa('[data-color-swatch]', selector).forEach((swatch) => { const active = selected && swatch.dataset.variantId === selected.value; swatch.classList.toggle('is-active', active); swatch.setAttribute('aria-checked', String(active)); });
      updateAddButton(info);
    });
  });

  qsa('[data-color-swatch]').forEach((swatch) => {
    swatch.addEventListener('click', () => {
      const selector = swatch.closest('[data-color-selector]');
      const select = selector && qs('[data-color-select]', selector);
      if (!select) return;
      select.value = swatch.dataset.variantId;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });
    swatch.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      const buttons = qsa('[data-color-swatch]:not([disabled])', swatch.closest('.swatch-list'));
      const index = buttons.indexOf(swatch);
      const direction = ['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1;
      const next = buttons[(index + direction + buttons.length) % buttons.length];
      if (next) { event.preventDefault(); next.focus(); next.click(); }
    });
  });

  qsa('[data-offer-picker]').forEach((picker) => {
    const info = picker.closest('.product-info');
    qsa('[data-offer-option]', picker).forEach((option) => option.addEventListener('click', () => {
      qsa('[data-offer-option]', picker).forEach((item) => { const active = item === option; item.classList.toggle('is-active', active); item.setAttribute('aria-checked', String(active)); });
      updateAddButton(info);
    }));
    updateAddButton(info);
  });

  qsa('.product-info form[action*="/cart/add"], .product-info form[action*="cart/add"]').forEach((form) => {
    form.addEventListener('submit', (event) => {
      if (event.submitter && event.submitter.classList.contains('shopify-payment-button__button')) return;
      if (!window.fetch || !window.Shopify) return;
      event.preventDefault();
      const button = event.submitter || qs('button[name="add"]', form);
      if (!button || button.disabled) return;
      lastCartTrigger = button;
      const label = qs('[data-add-label]', button) || qs('span', button);
      const originalLabel = label ? label.textContent : '';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (label) label.textContent = 'Adding…';
      const info = form.closest('.product-info');
      const offerSize = selectedOfferSize(info);
      const first = selectedColor(info, 1);
      const second = offerSize === 2 ? selectedColor(info, 2) : null;
      if (!first || !first.available || (offerSize === 2 && (!second || !second.available))) {
        showToast('Choose an available color for each alarm.', true);
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (label) label.textContent = originalLabel;
        return;
      }
      let request;
      if (offerSize === 2) {
        const quantities = new Map();
        [first, second].forEach((selection) => quantities.set(selection.id, (quantities.get(selection.id) || 0) + 1));
        const items = Array.from(quantities, ([id, quantity]) => ({ id: Number(id), quantity }));
        request = fetch(`${shopRoot()}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
      } else {
        request = fetch(`${shopRoot()}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      }
      request
        .then(async (response) => { if (!response.ok) { const payload = await response.json().catch(() => ({})); throw new Error(payload.description || 'Unable to add item'); } return response.json(); })
        .then((payload) => {
          const addedItems = Array.isArray(payload.items) ? payload.items : [payload];
          const analyticsItems = addedItems.map((item) => ({
            item_id: String(item.variant_id || item.id),
            item_name: item.product_title || info.closest('[data-product-section]')?.dataset.productTitle || 'PullAloud',
            item_variant: item.variant_title || '',
            price: Number(item.final_price || item.price || 0) / 100,
            quantity: Number(item.quantity || 1)
          }));
          const value = analyticsItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
          const addedCount = analyticsItems.reduce((sum, item) => sum + item.quantity, 0);
          pushCommerceEvent('add_to_cart', { currency: payload.currency || currency(), value, items: analyticsItems });
          announceShopifyEvent('shopify:cart:lines-update', { lines: addedItems });
          return refreshCartDrawer(true).catch(() => { updateCartLabels((cartState ? cartState.item_count : 0) + addedCount); showToast('Added to cart. Open your cart to continue.', false); });
        })
        .catch((error) => showToast(error.message || 'We couldn’t add this item. Please try again.', true))
        .finally(() => { button.disabled = false; button.removeAttribute('aria-busy'); if (label) label.textContent = originalLabel; updateAddButton(form.closest('.product-info')); });
    });
  });

  qsa('[data-mobile-buy-bar]').forEach((bar) => {
    const section = bar.previousElementSibling;
    const mainButton = section && qs('[data-add-button]', section);
    if (!mainButton || !('IntersectionObserver' in window)) return;
    bar.hidden = false;
    let mainButtonVisible = true;
    let footerVisible = false;
    const syncBar = () => {
      const shouldShow = !mainButtonVisible && !footerVisible;
      bar.classList.toggle('is-visible', shouldShow);
      document.body.classList.toggle('has-mobile-buy-bar', shouldShow);
    };
    const buttonObserver = new IntersectionObserver((entries) => { mainButtonVisible = entries[0].isIntersecting; syncBar(); }, { threshold: 0.15 });
    buttonObserver.observe(mainButton);
    const footer = qs('.site-footer');
    if (footer) { const footerObserver = new IntersectionObserver((entries) => { footerVisible = entries[0].isIntersecting; syncBar(); }, { threshold: 0 }); footerObserver.observe(footer); }
  });

  qsa('[data-mobile-cart-bar]').forEach((bar) => {
    const primaryCheckout = qs('[data-cart-primary-checkout]');
    if (!primaryCheckout || !('IntersectionObserver' in window)) return;
    bar.hidden = false;
    const observer = new IntersectionObserver((entries) => {
      const shouldShow = !entries[0].isIntersecting;
      bar.classList.toggle('is-visible', shouldShow);
      document.body.classList.toggle('has-mobile-cart-bar', shouldShow);
    }, { threshold: 0.2 });
    observer.observe(primaryCheckout);
  });

  if (document.body.classList.contains('template-cart')) fetchCart().catch(() => {});
})();
